/**
 * FeaturesSection Component
 *
 * Features showcase with phone mockups and feature cards.
 */

import { library, IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolderOpen,
  faCloudArrowDown,
  faStream,
  faHandshake,
  faRobot,
  faUserLock,
  faShareNodes,
  faShieldHalved,
  faBell,
  faMicrophone,
  faUserTie,
  faChartPie,
  faUserMd,
  faGlobe,
  faArrowRight,
  faUser,
  faCheck,
  faCheckCircle,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faApple, faAndroid } from '@fortawesome/free-brands-svg-icons';
import { useScrollAnimation, useTerminalEffect, useUsageBarAnimation, useVideoLoad } from '../hooks';
import { FadeIn } from './FadeIn';

// Register icons for this component
library.add(
  faFolderOpen,
  faCloudArrowDown,
  faStream,
  faHandshake,
  faRobot,
  faUserLock,
  faShareNodes,
  faShieldHalved,
  faBell,
  faMicrophone,
  faUserTie,
  faChartPie,
  faUserMd,
  faGlobe,
  faArrowRight,
  faUser,
  faCheck,
  faCheckCircle,
  faUserPlus,
  faApple,
  faAndroid
);

// Small Feature Card Component
function SmallFeatureCard({
  icon,
  title,
  description,
  gradient = 'from-blue-50/50 dark:from-blue-500/10',
  children,
}: {
  icon: IconProp;
  title: string;
  description: string;
  gradient?: string;
  children?: React.ReactNode;
}) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref as any}
      className={`rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col gap-2 group hover:border-primary/50 transition-colors relative overflow-hidden transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} to-transparent pointer-events-none`} />
      <div className="relative z-10 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
        <FontAwesomeIcon icon={icon} />
      </div>
      <h3 className="relative z-10 font-bold">{title}</h3>
      <p className="relative z-10 text-xs text-muted-foreground">{description}</p>
      {children && <div className="relative z-10">{children}</div>}
    </div>
  );
}

// Phone-mockup video with one retry before falling back to an icon placeholder.
// See useVideoLoad for the rationale (inconsistent partial-content caching).
function PhoneMockupVideo({
  src,
  fallbackIcon,
  fallbackLabel,
  fallbackIconClass,
}: {
  src: string;
  fallbackIcon: IconProp;
  fallbackLabel: string;
  fallbackIconClass: string;
}) {
  const { failed, retryKey, handleLoadedData, handleError } = useVideoLoad();

  if (failed) {
    return (
      <div className="text-center p-4">
        <FontAwesomeIcon icon={fallbackIcon} className={`text-4xl mb-3 ${fallbackIconClass}`} />
        <p className="text-xs text-muted-foreground font-medium">{fallbackLabel}</p>
      </div>
    );
  }

  return (
    <video
      key={retryKey}
      className="absolute inset-0 w-full h-full object-cover"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      onLoadedData={handleLoadedData}
      onError={handleError}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}

export function FeaturesSection() {
  const assetsUrl = import.meta.env.VITE_PUBLIC_ASSETS_URL || '';

  return (
    <section id="features" className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
      <FadeIn>
        {/* Section Header */}
        <div className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center mb-12">
          <h2 className="font-heading text-3xl font-bold leading-[1.1] sm:text-3xl md:text-6xl">
            Tudo em um só lugar
          </h2>
          <p className="max-w-[85%] leading-normal text-muted-foreground sm:text-lg sm:leading-7">
            Ferramentas completas para simplificar a rotina de médicos e pacientes.
          </p>

          {/* Phone Mockups Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 w-full">
            {/* Timeline Screen */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="w-full max-w-[24rem] md:max-w-none aspect-[9/16] rounded-2xl border-4 border-border bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 shadow-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-6 bg-border/20 rounded-t-xl z-10"></div>
                <PhoneMockupVideo
                  src={`${assetsUrl}/cdn/public/video-timeline.mp4`}
                  fallbackIcon={faStream}
                  fallbackLabel="Linha do Tempo"
                  fallbackIconClass="text-primary/30"
                />
              </div>
              <p className="text-sm font-medium">Linha do Tempo</p>
            </div>

            {/* AI Extraction Screen */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="w-full max-w-[24rem] md:max-w-none aspect-[9/16] rounded-2xl border-4 border-border bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-950/20 dark:to-teal-950/20 shadow-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-6 bg-border/20 rounded-t-xl z-10"></div>
                <PhoneMockupVideo
                  src={`${assetsUrl}/cdn/public/video-exams.mp4`}
                  fallbackIcon={faRobot}
                  fallbackLabel="Extração IA"
                  fallbackIconClass="text-secondary/30"
                />
              </div>
              <p className="text-sm font-medium">Extração de Exames</p>
            </div>

            {/* Consultations records Screen */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="w-full max-w-[24rem] md:max-w-none aspect-[9/16] rounded-2xl border-4 border-border bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-950/20 dark:to-teal-950/20 shadow-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-6 bg-border/20 rounded-t-xl z-10"></div>
                <PhoneMockupVideo
                  src={`${assetsUrl}/cdn/public/video-records.mp4`}
                  fallbackIcon={faRobot}
                  fallbackLabel="Gravação IA"
                  fallbackIconClass="text-secondary/30"
                />
              </div>
              <p className="text-sm font-medium">Gravação de Consultas</p>
            </div>

            {/* Connection Screen */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="w-full max-w-[24rem] md:max-w-none aspect-[9/16] rounded-2xl border-4 border-border bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20 shadow-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-6 bg-border/20 rounded-t-xl z-10"></div>
                <PhoneMockupVideo
                  src={`${assetsUrl}/cdn/public/video-connections.mp4`}
                  fallbackIcon={faHandshake}
                  fallbackLabel="Conexões"
                  fallbackIconClass="text-cyan-500/30"
                />
              </div>
              <p className="text-sm font-medium">Conexão Médico-Paciente</p>
            </div>
          </div>
        </div>
      </FadeIn>

      {/* Grid Row 1 */}
      <FadeIn className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Main Feature: Centralized management */}
        <div className="sm:col-span-2 md:col-span-1 rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col overflow-hidden relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent dark:from-blue-500/10 pointer-events-none" />
          <div className="relative z-10">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
              <FontAwesomeIcon icon={faFolderOpen} className="text-2xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">Organizador Online</h3>
            <p className="text-muted-foreground text-sm">
              Gerencie exames, consultas e receitas em um histórico digital centralizado.
            </p>
          </div>
          {/* Visual decoration */}
          <div className="mt-4 rounded-lg border bg-background shadow-sm p-3 space-y-2">
            <div className="h-3 w-3/4 bg-muted rounded"></div>
            <div className="h-3 w-1/2 bg-muted rounded"></div>
            <div className="h-10 w-full bg-blue-50/50 dark:bg-blue-900/20 rounded mt-2 border border-blue-100 dark:border-blue-900 border-dashed flex items-center justify-center">
              <span className="text-xs text-blue-400 dark:text-blue-300">Arraste seus exames aqui</span>
            </div>
          </div>
        </div>

        {/* Platform Availability */}
        <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-indigo-50/50 dark:to-indigo-500/10 pointer-events-none" />
          <div className="relative z-10">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-2">
              <FontAwesomeIcon icon={faCloudArrowDown} />
            </div>
            <h3 className="font-bold">Disponível em todo lugar</h3>
            <p className="text-xs text-muted-foreground">
              Acesse via Web, iOS ou Android. Seus dados sempre sincronizados.
            </p>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-background border shadow-sm flex items-center justify-center mb-1">
                  <FontAwesomeIcon icon={faApple} className="text-xl" />
                </div>
                <span className="text-[10px] text-muted-foreground font-medium">iOS</span>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-background border shadow-sm flex items-center justify-center mb-1">
                  <FontAwesomeIcon icon={faAndroid} className="text-xl text-green-600" />
                </div>
                <span className="text-[10px] text-muted-foreground font-medium">Android</span>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-background border shadow-sm flex items-center justify-center mb-1">
                  <FontAwesomeIcon icon={faGlobe} className="text-xl text-blue-500" />
                </div>
                <span className="text-[10px] text-muted-foreground font-medium">Web</span>
              </div>
            </div>
          </div>
        </div>

        {/* Small Feature: Timeline */}
        <SmallFeatureCard
          icon={faStream}
          title="Linha do Tempo"
          description="Visualize toda sua jornada cronologicamente."
          gradient="from-purple-50/50 dark:from-purple-500/10"
        >
          {/* Mini timeline mockup */}
          <div className="mt-2">
            <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-primary/20"></div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2 pl-5 relative">
                <div className="absolute left-1 w-2 h-2 rounded-full bg-primary"></div>
                <span className="text-muted-foreground">Jan 2026</span>
                <span className="text-primary font-medium">Hemograma</span>
              </div>
              <div className="flex items-center gap-2 pl-5 relative">
                <div className="absolute left-1 w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-muted-foreground">Dez 2025</span>
                <span className="text-green-600 font-medium">Consulta</span>
              </div>
              <div className="flex items-center gap-2 pl-5 relative">
                <div className="absolute left-1 w-2 h-2 rounded-full bg-indigo-500"></div>
                <span className="text-muted-foreground">Nov 2025</span>
                <span className="text-indigo-600 font-medium">Vacina</span>
              </div>
              <div className="flex items-center gap-2 pl-5 relative">
                <div className="absolute left-1 w-2 h-2 rounded-full bg-orange-500"></div>
                <span className="text-muted-foreground">Out 2025</span>
                <span className="text-orange-600 font-medium">Raio-X</span>
              </div>
              <div className="flex items-center gap-2 pl-5 relative">
                <div className="absolute left-1 w-2 h-2 rounded-full bg-teal-500"></div>
                <span className="text-muted-foreground">Set 2025</span>
                <span className="text-teal-600 font-medium">Ecocardiograma</span>
              </div>
            </div>
          </div>
        </SmallFeatureCard>

        {/* Connection Doctor-Patient */}
        <div className="sm:col-span-2 md:col-span-2 rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-transparent dark:from-cyan-500/10 pointer-events-none" />
          <div className="relative z-10 mb-3">
            <div className="h-12 w-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center mb-4 text-cyan-600 dark:text-cyan-400">
              <FontAwesomeIcon icon={faHandshake} className="text-2xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">Conexões Médico-Paciente</h3>
            <p className="text-muted-foreground text-sm">
              O acesso a dados médicos requer uma relação explícita no sistema, assegurando total privacidade ao
              paciente.
            </p>
          </div>
          {/* Connection workflow mockup */}
          <div className="flex items-center justify-center gap-3 py-2">
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                <FontAwesomeIcon icon={faUserMd} />
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground">Médico</span>
            </div>
            <div className="flex items-center">
              <div className="h-0.5 w-4 sm:w-6 bg-muted"></div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-secondary flex items-center justify-center text-white text-xs">
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
              <div className="h-0.5 w-4 sm:w-6 bg-muted"></div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground">Paciente</span>
            </div>
            <div className="flex items-center">
              <div className="h-0.5 w-4 sm:w-6 bg-muted"></div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <div className="h-0.5 w-4 sm:w-6 bg-muted"></div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                <FontAwesomeIcon icon={faFolderOpen} />
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground">Acesso Seguro</span>
            </div>
          </div>
        </div>

        {/* Secondary Feature: AI Analysis */}
        <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-b from-green-50/50 to-transparent dark:from-green-500/10 pointer-events-none" />
          <div className="relative z-10 mb-3">
            <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4 text-secondary">
              <FontAwesomeIcon icon={faRobot} className="text-2xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">Leitura Automática de Exames com IA</h3>
            <p className="text-muted-foreground text-sm">
              Envie PDFs ou imagens. Nossa IA identifica automaticamente resultados, valores de referência e datas, estruturando tudo no histórico digital.
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-xs font-mono text-muted-foreground">
            <TypingEffect />
          </div>
        </div>

        {/* Security: Access Control */}
        <SmallFeatureCard icon={faUserLock} title="Trilha de Auditoria" description="Acesso baseado em relacionamentos aprovados. Você controla quem vê seus dados." />

        {/* Small Feature: Sharing */}
        <SmallFeatureCard
          icon={faShareNodes}
          title="Compartilhamento Seguro"
          description="Controle quem acessa seus dados. Você decide o que compartilhar."
          gradient="from-green-50/50 dark:from-green-500/10"
        />

        {/* Security: LGPD Compliance */}
        <SmallFeatureCard
          icon={faShieldHalved}
          title="Conforme LGPD"
          description="Arquitetura projetada para proteção de dados de saúde conforme a Lei Geral de Proteção de Dados."
        />
      </FadeIn>

      {/* Section: Para Profissionais */}
      <FadeIn>
        <div className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center my-16">
          <div className="rounded-2xl bg-secondary/10 px-4 py-1.5 text-sm font-medium text-secondary">
            Para Profissionais
          </div>
          <h2 className="font-heading text-2xl font-bold leading-[1.1] sm:text-3xl md:text-4xl">
            Ferramentas para Clínicas e Consultórios
          </h2>
        </div>
      </FadeIn>

      {/* Grid Row 2: Professional Features */}
      <FadeIn className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Small Feature: Notifications */}
        <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col gap-2 group hover:border-red-200 transition-colors relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-transparent dark:from-red-500/10 pointer-events-none" />
          <div className="relative z-10 h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-500 dark:text-red-400 mb-2">
            <FontAwesomeIcon icon={faBell} />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center">
              3
            </span>
          </div>
          <h3 className="relative z-10 font-bold">Alertas e Notificações</h3>
          <p className="relative z-10 text-xs text-muted-foreground">Notificações sobre novos exames, conexões e documentos processados.</p>
          {/* Notification examples */}
          <div className="relative z-10 space-y-2 mt-2">
            <div className="p-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 rounded-lg text-xs flex gap-2 items-start">
              <FontAwesomeIcon icon={faUserPlus} className="text-primary mt-0.5" />
              <div>
                <div className="font-medium text-foreground">Novo pedido de conexão</div>
                <div className="text-muted-foreground text-[10px]">Maria Silva aceitou o seu pedido</div>
              </div>
            </div>
            <div className="p-2 bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900 rounded-lg text-xs flex gap-2 items-start">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-0.5" />
              <div>
                <div className="font-medium text-foreground">Exame processado</div>
                <div className="text-muted-foreground text-[10px]">Hemograma extraído</div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Transcription with Waveform */}
        <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent dark:from-purple-500/10 pointer-events-none" />
          <div className="relative z-10 mb-3">
            <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-3 text-purple-600 dark:text-purple-400">
              <FontAwesomeIcon icon={faMicrophone} className="text-xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-1">Transcrição Automática de Consultas</h3>
            <p className="text-muted-foreground text-sm">Grave o áudio da consulta e receba a transcrição automática, gerando um registro estruturado sem digitação.</p>
          </div>
          {/* Waveform + Chat mockup */}
          <div className="flex flex-col gap-3">
            <div className="h-10 bg-muted/50 rounded-lg flex items-center justify-center gap-1 waveform-container">
              <div className="waveform-bar h-4 w-1 bg-purple-400 rounded-full"></div>
              <div className="waveform-bar h-6 w-1 bg-purple-500 rounded-full"></div>
              <div className="waveform-bar h-8 w-1 bg-purple-600 rounded-full"></div>
              <div className="waveform-bar h-5 w-1 bg-purple-500 rounded-full"></div>
              <div className="waveform-bar h-10 w-1 bg-purple-600 rounded-full"></div>
              <div className="waveform-bar h-6 w-1 bg-purple-500 rounded-full"></div>
              <div className="waveform-bar h-4 w-1 bg-purple-400 rounded-full"></div>
            </div>
            <div className="space-y-2">
              <div className="flex gap-2 items-start">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-xs shrink-0">
                  P
                </div>
                <div className="bg-primary/10 dark:bg-primary/20 rounded-lg p-2 text-xs">
                  <span className="text-primary dark:text-primary-foreground font-medium">Profissional:</span> Como está se sentindo?
                </div>
              </div>
              <div className="flex gap-2 items-start justify-end">
                <div className="bg-muted dark:bg-muted/30 rounded-lg p-2 text-xs">
                  <span className="text-muted-foreground dark:text-muted-foreground font-medium">Paciente:</span> Melhor, a dor diminuiu.
                </div>
                <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-white text-xs shrink-0">
                  P
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chatbot IA */}
        <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 to-transparent dark:from-blue-500/10 pointer-events-none" />
          <div className="relative z-10 mb-3">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
              <FontAwesomeIcon icon={faRobot} className="text-2xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">Assistente IA para Análise de Dados</h3>
            <p className="text-muted-foreground text-sm">
              Chatbot inteligente para análise de exames, interpretação de resultados e geração de relatórios clínicos.
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-2">
            <div className="flex gap-2 items-start">
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-[10px] shrink-0">
                <FontAwesomeIcon icon={faRobot} />
              </div>
              <div className="bg-primary/10 dark:bg-primary/20 rounded-lg p-2 flex-1">
                <span className="text-primary dark:text-primary-foreground font-medium">Bot:</span> Gerei o relatório de Outubro com 127 exames
                processados.
              </div>
            </div>
          </div>
        </div>

        {/* Secretary Management */}
        <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="relative z-10 mb-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3 text-amber-600 dark:text-amber-400">
              <FontAwesomeIcon icon={faUserTie} className="text-xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-1">Acesso de Secretariado</h3>
            <p className="text-muted-foreground text-sm">
              Delegue a leitura de exames e documentos para secretárias com controle de escopo e registro de auditoria completo.
            </p>
          </div>
          {/* User list mockup */}
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-amber-200 dark:bg-amber-900 flex items-center justify-center text-amber-700 dark:text-amber-300 text-xs font-bold">
                AM
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate text-foreground">Ana Maria</div>
                <div className="text-[10px] text-muted-foreground">Secretária</div>
              </div>
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-amber-200 dark:bg-amber-900 flex items-center justify-center text-amber-700 dark:text-amber-300 text-xs font-bold">
                JO
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate text-foreground">João</div>
                <div className="text-[10px] text-muted-foreground">Secretário</div>
              </div>
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
            </div>
          </div>
        </div>

        {/* Usage Dashboard */}
        <div className="sm:col-span-2 md:col-span-2 rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col relative overflow-hidden group">
          <div className="relative z-10 mb-6">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-3 text-indigo-600 dark:text-indigo-400">
              <FontAwesomeIcon icon={faChartPie} className="text-xl" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-1">Painel de Controle da Clínica</h3>
            <p className="text-muted-foreground text-sm">
              Monitore a quota de extrações com IA, armazenamento de exames e assentos da sua clínica com precisão em tempo real.
            </p>
          </div>
          {/* Usage bars mockup */}
          <UsageBars />
        </div>
      </FadeIn>
    </section>
  );
}

const AI_TYPING_LINES = [
  "> Analisando hemograma...",
  "> Plaquetas: Normal",
  "> Leucócitos: Normal",
  "> Sugestão: Verificar vitamina D"
];

// Typing Effect Component for AI feature
function TypingEffect() {
  const { displayText, elementRef } = useTerminalEffect(AI_TYPING_LINES);

  return (
    <div ref={elementRef} className="typing-effect whitespace-pre-line text-left flex-1 min-h-[4rem]">
      {displayText}
      <span className="animate-pulse">_</span>
    </div>
  );
}



// Usage Bars Component
function UsageBars() {
  return (
    <div className="flex-1 grid grid-cols-2 gap-4">
      <UsageBar label="Extrações IA" value="42/100" percentage={42} color="bg-primary" />
      <UsageBar label="Armazenamento" value="2.4GB/10GB" percentage={24} color="bg-secondary" />
      <UsageBar label="Assentos" value="5/50" percentage={10} color="bg-purple-500" />
      <UsageBar label="Conexões" value="90/100" percentage={90} color="bg-cyan-500" />
    </div>
  );
}

// Usage Bar Component
function UsageBar({
  label,
  value,
  percentage,
  color,
}: {
  label: string;
  value: string;
  percentage: number;
  color: string;
}) {
  const { ref } = useUsageBarAnimation(percentage);

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div ref={ref} className={`h-full ${color} rounded-full transition-all duration-1000 ease-out`} style={{ width: '0%' }} />
      </div>
    </div>
  );
}
